/**
 * Button Primitive
 * Semantic button component with strict typing for design tokens
 */

import React from "react";
import { Button as ChakraButton, Spinner } from "@chakra-ui/react";
import { resolveSpacing } from "../foundation";
import { Color } from "../foundation/Color";
import { Spacing } from "../foundation/Spacing";
import { Shadow } from "../foundation/Shadow";
import { Radius } from "../foundation/Radius";
import { useColorModeValue } from "../hooks/useColorModeValue";
import { Icon, IconTypeToken } from "./Icon";

// Branded types for button tokens
declare const __button_intent_brand: unique symbol;
declare const __button_variant_brand: unique symbol;

export type ButtonSizeToken = "sm" | "md" | "lg" | "base";

export interface ButtonIntentToken {
  readonly [__button_intent_brand]: true;
  readonly _value: string;
}

export interface ButtonVariantToken {
  readonly [__button_variant_brand]: true;
  readonly _value: string;
}

// Factory functions
const createButtonIntentToken = (value: string): ButtonIntentToken => ({ _value: value }) as ButtonIntentToken;
const createButtonVariantToken = (value: string): ButtonVariantToken => ({ _value: value }) as ButtonVariantToken;

export interface ButtonProps {
  children: React.ReactNode;
  intent?: ButtonIntentToken;
  size?: ButtonSizeToken;
  variant?: ButtonVariantToken;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  leftIcon?: IconTypeToken | { customIcon: React.ReactNode };
  rightIcon?: IconTypeToken | { customIcon: React.ReactNode };
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit" | "reset";
  form?: string;
  "data-testid"?: string;
}

// Component implementation
const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      intent,
      size,
      variant,
      loading = false,
      disabled = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      onClick,
      type = "button",
      form,
      "data-testid": testId,
      // Runtime forwarding for composition wrappers (MenuButton as={Button}); typed API stays closed.
      ...rest
    },
    ref,
  ) => {
    // Set defaults inside the component to avoid circular reference issues
    const resolvedIntent = intent || buttonTokens.intent.primary;
    const resolvedSize = size || buttonTokens.size.md;
    const resolvedVariant = variant || buttonTokens.variant.solid;

    // Resolve all possible colors at the top level to avoid conditional hook calls
    const primaryBg = useColorModeValue(Color.interactive.primary);
    const primaryColor = useColorModeValue(Color.text.inverse);
    const primaryHoverBg = useColorModeValue(Color.interactive.primaryHover);
    const primaryActiveBg = useColorModeValue(Color.interactive.primaryActive);
    const secondaryBg = useColorModeValue(Color.surface.secondary);
    const secondaryHoverBg = useColorModeValue(Color.surface.tertiary);
    const textPrimary = useColorModeValue(Color.text.primary);
    const successBg = useColorModeValue(Color.primitive.green(500));
    const successHoverBg = useColorModeValue(Color.primitive.green(600));
    const errorBg = useColorModeValue(Color.primitive.red(500));
    const errorHoverBg = useColorModeValue(Color.primitive.red(600));
    const indigoBg = useColorModeValue(Color.primitive.indigo(400));
    const indigoHoverBg = useColorModeValue(Color.primitive.indigo(500));
    const neutralBorder = useColorModeValue(Color.primitive.gray(300));
    const focusShadow = useColorModeValue(Shadow.focus._lightValue, Shadow.focus._darkValue);

    // Resolve intent colors based on variant
    const getIntentColors = (intent: ButtonIntentToken, variant: ButtonVariantToken) => {
      const intentValue = intent._value;
      const variantValue = variant._value;

      if (intentValue === "primary") {
        if (variantValue === "solid") {
          return {
            bg: primaryBg,
            color: primaryColor,
            _hover: { bg: primaryHoverBg },
            _active: { bg: primaryActiveBg },
          };
        } else if (variantValue === "outline") {
          return {
            bg: "transparent",
            color: primaryBg,
            borderWidth: "1px",
            borderColor: primaryBg,
            _hover: { bg: primaryBg, color: primaryColor },
          };
        } else if (variantValue === "ghost") {
          return {
            bg: "transparent",
            color: "content.primary",
            _hover: { bg: "bg.secondary" },
          };
        }
      } else if (intentValue === "secondary") {
        if (variantValue === "solid") {
          return {
            bg: secondaryBg,
            color: textPrimary,
            _hover: { bg: secondaryHoverBg },
          };
        } else if (variantValue === "outline") {
          return {
            bg: "transparent",
            color: textPrimary,
            borderColor: neutralBorder,
            borderWidth: "1px",
            _hover: { bg: secondaryBg },
          };
        }
      } else if (intentValue === "success") {
        if (variantValue === "solid") {
          return {
            bg: successBg,
            color: primaryColor,
            _hover: { bg: successHoverBg },
          };
        }
      } else if (intentValue === "error") {
        if (variantValue === "solid") {
          return {
            bg: errorBg,
            color: primaryColor,
            _hover: { bg: errorHoverBg },
          };
        }
      } else if (intentValue === "indigo") {
        if (variantValue === "solid") {
          return {
            bg: indigoBg,
            color: primaryColor,
            _hover: { bg: indigoHoverBg },
          };
        } else if (variantValue === "outline") {
          return {
            bg: "transparent",
            color: indigoBg,
            borderWidth: "1px",
            borderColor: indigoBg,
            _hover: { bg: indigoBg, color: primaryColor },
          };
        }
      }

      // Default fallback
      return {
        bg: primaryBg,
        color: primaryColor,
        _hover: { bg: primaryHoverBg },
      };
    };

    const getSizeProps = (size: ButtonSizeToken) => {
      switch (size) {
        case "sm":
          return {
            size: "sm" as const,
            px: resolveSpacing(Spacing.small),
            py: resolveSpacing(Spacing.tiny),
          };
        case "lg":
          return {
            size: "lg" as const,
            px: resolveSpacing(Spacing.large),
            py: resolveSpacing(Spacing.small),
          };
        case "md":
        case "base":
        default:
          return {
            size: "md" as const,
            px: resolveSpacing(Spacing.medium),
            py: resolveSpacing(Spacing.small),
          };
      }
    };

    const intentColors = getIntentColors(resolvedIntent, resolvedVariant);
    const sizeProps = getSizeProps(resolvedSize);

    // Helper function to render icons
    const renderIcon = (iconProp?: IconTypeToken | { customIcon: React.ReactNode }): React.ReactElement | undefined => {
      if (!iconProp) return undefined;

      if (typeof iconProp === "object" && "customIcon" in iconProp) {
        return iconProp.customIcon as React.ReactElement;
      }

      // Determine icon size based on button size
      const iconSize = resolvedSize === "sm" ? Icon.size.sm : resolvedSize === "lg" ? Icon.size.lg : Icon.size.base;

      return <Icon type={iconProp} size={iconSize} />;
    };

    return (
      <ChakraButton
        ref={ref}
        {...intentColors}
        {...sizeProps}
        isLoading={loading}
        isDisabled={disabled}
        width={fullWidth ? "full" : undefined}
        leftIcon={renderIcon(leftIcon)}
        rightIcon={renderIcon(rightIcon)}
        onClick={onClick}
        type={type}
        form={form}
        data-testid={testId}
        borderRadius={Radius.button._value}
        fontWeight="medium"
        transition="all 0.2s"
        _disabled={{
          opacity: 0.6,
          cursor: "not-allowed",
          _hover: {},
        }}
        _focus={{
          boxShadow: focusShadow,
        }}
        spinner={<Spinner size={resolvedSize === "sm" ? "xs" : "sm"} />}
        {...rest}
      >
        {children}
      </ChakraButton>
    );
  },
);

ButtonComponent.displayName = "Button";

// Button tokens
const buttonTokens = {
  size: {
    sm: "sm" as const,
    md: "md" as const,
    lg: "lg" as const,
    base: "base" as const,
  } as const,

  intent: {
    primary: createButtonIntentToken("primary"),
    secondary: createButtonIntentToken("secondary"),
    tertiary: createButtonIntentToken("tertiary"),
    success: createButtonIntentToken("success"),
    warning: createButtonIntentToken("warning"),
    error: createButtonIntentToken("error"),
    indigo: createButtonIntentToken("indigo"),
  } as const,

  variant: {
    solid: createButtonVariantToken("solid"),
    outline: createButtonVariantToken("outline"),
    ghost: createButtonVariantToken("ghost"),
    link: createButtonVariantToken("link"),
  } as const,
};

// Public API - combines tokens and component
export const Button = Object.assign(ButtonComponent, buttonTokens);
